export interface CropAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropOutputOptions {
  /** Longest output edge in px; the crop is downscaled to fit. Default 1024. */
  maxSize?: number;
  /** Output mime type. Default "image/jpeg". */
  mimeType?: string;
  /** Quality 0..1 for lossy types. Default 0.92. */
  quality?: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Lets the canvas read pixels from cross-origin sources that send CORS
    // headers; harmless for the blob: URLs we use for freshly-picked files.
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for cropping"));
    img.src = src;
  });
}

/** Renders the selected region of an image to a canvas and returns it as a Blob. */
export async function getCroppedBlob(
  imageSrc: string,
  area: CropAreaPixels,
  options: CropOutputOptions = {},
): Promise<Blob> {
  const { maxSize = 1024, mimeType = "image/jpeg", quality = 0.92 } = options;
  const image = await loadImage(imageSrc);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas 2d context");

  const scale = Math.min(1, maxSize / Math.max(area.width, area.height));
  canvas.width = Math.max(1, Math.round(area.width * scale));
  canvas.height = Math.max(1, Math.round(area.height * scale));

  // JPEG has no alpha channel; paint a white backdrop so transparent source
  // pixels don't turn black.
  if (mimeType === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to generate cropped image"))),
      mimeType,
      quality,
    );
  });
}

export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type });
}
